import React, { useState, useRef, useEffect } from 'react';
import { gallery as fallbackGallery } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, X, Grid, LayoutGrid, Expand } from 'lucide-react';
import axios from 'axios';
import { optimized, srcSetFor } from '../utils/img';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lazy loading image component with blur placeholder
const LazyImage = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="relative w-full h-full overflow-hidden bg-slate-200">
      {/* Blur placeholder */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 transition-opacity duration-500 ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent" 
          style={{ animation: 'shimmer 1.5s infinite' }} 
        />
      </div>
      
      {/* Actual image - only load when in view */}
      {isInView && !hasError && (
        <img
          src={optimized(src, 800)}
          srcSet={srcSetFor(src)}
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          onClick={onClick}
          className={`${className} transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
      
      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <span className="text-slate-400 text-sm">Image unavailable</span>
        </div>
      )}
    </div>
  );
};

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filter, setFilter] = useState('All');
  const [visibleCount, setVisibleCount] = useState(12);
  const [viewMode, setViewMode] = useState('grid');
  const [gallery, setGallery] = useState(fallbackGallery);
  const { t } = useLanguage();

  // Fetch gallery from API on mount
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/gallery`);
        if (response.data && response.data.length > 0) {
          setGallery(response.data);
        }
      } catch (error) {
        console.error('Error fetching gallery from API, using fallback:', error);
      }
    };
    fetchGallery();
  }, []);

  // Translated categories mapping
  const categoryMap = {
    'Pool': t('gallery.pool'),
    'Terrace': t('gallery.terrace'),
    'Exterior': t('gallery.exterior'),
    'Rooms': t('gallery.rooms'),
    'Apartment': t('gallery.apartment'),
    'Dining': t('gallery.dining'),
    'Bathroom': t('gallery.bathroom')
  };

  // Get unique categories from gallery data
  const uniqueCategories = [...new Set(gallery.map(item => item.category))];
  const translatedCategories = [t('gallery.all'), ...uniqueCategories.map(cat => categoryMap[cat] || cat)];
  
  const getFilteredGallery = () => {
    if (filter === t('gallery.all')) return gallery;
    
    // Find original category key from translated value
    const originalCategory = Object.keys(categoryMap).find(key => categoryMap[key] === filter);
    return gallery.filter(item => item.category === originalCategory);
  };

  const filteredGallery = getFilteredGallery();
  const visibleGallery = filteredGallery.slice(0, visibleCount);
  const hasMore = visibleCount < filteredGallery.length;

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + 12, filteredGallery.length));
  };

  const showAll = () => {
    setVisibleCount(filteredGallery.length);
  };

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(12);
  }, [filter]);

  // Lightbox navigation
  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = selectedIndex === 0 ? filteredGallery.length - 1 : selectedIndex - 1;
    setSelectedIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = selectedIndex === filteredGallery.length - 1 ? 0 : selectedIndex + 1;
    setSelectedIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowLeft') goToPrevious(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedIndex]);

  // Get category count
  const getCategoryCount = (category) => {
    if (category === t('gallery.all')) return gallery.length;
    const originalCategory = Object.keys(categoryMap).find(key => categoryMap[key] === category);
    return gallery.filter(item => item.category === originalCategory).length;
  };

  return (
    <section id="gallery" className="py-24 bg-white">
      {/* Shimmer animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">{t('gallery.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-800 mt-3 mb-4">{t('gallery.title')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t('gallery.description')}
          </p>
          <p className="text-amber-600 font-medium mt-2">
            {gallery.length} {t('rooms.photos') || 'photos'}
          </p>
        </div>

        {/* Filter Tabs & View Toggle */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          {/* Filter Tabs - Scrollable on mobile */}
          <div className="w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <div className="flex gap-2 min-w-max px-1">
              {translatedCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                    filter === category
                      ? 'bg-amber-700 text-white shadow-lg shadow-amber-500/25'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`gallery-filter-${category}`}
                >
                  {category}
                  <span className={`ml-1.5 text-xs ${filter === category ? 'text-amber-200' : 'text-slate-400'}`}>
                    ({getCategoryCount(category)})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
              title="Grid view"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('masonry')}
              className={`p-2 rounded-md transition-all ${viewMode === 'masonry' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
              title="Masonry view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        {viewMode === 'grid' ? (
          // Standard Grid View - Responsive
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {visibleGallery.map((item, index) => (
              <div
                key={item.id}
                className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group"
                data-testid={`gallery-item-${index}`}
              >
                <LazyImage
                  src={item.image}
                  alt={categoryMap[item.category] || item.category}
                  onClick={() => openLightbox(item, index)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-pointer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 pointer-events-none flex items-center justify-between">
                  <span className="inline-block bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {categoryMap[item.category] || item.category}
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                    <Expand className="w-4 h-4 text-white" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Masonry View - Different heights
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 space-y-3 sm:space-y-4">
            {visibleGallery.map((item, index) => {
              // Vary heights for masonry effect
              const heightClass = index % 5 === 0 ? 'h-80' : index % 3 === 0 ? 'h-64' : 'h-52';
              return (
                <div
                  key={item.id}
                  className={`relative ${heightClass} overflow-hidden rounded-xl cursor-pointer group break-inside-avoid mb-3 sm:mb-4`}
                  data-testid={`gallery-item-${index}`}
                >
                  <LazyImage
                    src={item.image}
                    alt={categoryMap[item.category] || item.category}
                    onClick={() => openLightbox(item, index)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-pointer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 pointer-events-none flex items-center justify-between">
                    <span className="inline-block bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {categoryMap[item.category] || item.category}
                    </span>
                    <span className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                      <Expand className="w-4 h-4 text-white" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More / Show All */}
        {hasMore && (
          <div className="text-center mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={loadMore}
              className="px-8 py-3 bg-amber-700 hover:bg-amber-800 text-white rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="load-more-btn"
            >
              {t('gallery.loadMore') || 'Load More'} (+12)
            </button>
            <button
              onClick={showAll}
              className="px-8 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-all duration-300"
              data-testid="show-all-btn"
            >
              {t('gallery.showAll') || 'Show All'} ({filteredGallery.length})
            </button>
          </div>
        )}

        {/* Image Counter */}
        <div className="text-center mt-6 text-slate-500 text-sm">
          {t('gallery.showing') || 'Showing'} {visibleGallery.length} {t('gallery.of') || 'of'} {filteredGallery.length} {t('gallery.images') || 'images'}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 bg-white/10 rounded-full transition-colors z-10"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation arrows */}
            <button
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
              onClick={goToPrevious}
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
            <button
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
              onClick={goToNext}
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>

            {/* Image */}
            <div className="max-w-[95vw] max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
              <img
                src={optimized(selectedImage.image, 1600)}
                srcSet={srcSetFor(selectedImage.image, [800, 1200, 1600])}
                sizes="95vw"
                alt={categoryMap[selectedImage.category] || selectedImage.category}
                loading="eager"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Image info */}
            <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
              <span className="bg-amber-500 text-white px-4 sm:px-6 py-2 rounded-full font-medium text-sm sm:text-base">
                {categoryMap[selectedImage.category] || selectedImage.category}
              </span>
              <span className="text-white/60 text-xs sm:text-sm">
                {selectedIndex + 1} / {filteredGallery.length}
              </span>
            </div>

            {/* Thumbnail strip - hidden on mobile */}
            <div className="hidden lg:flex absolute bottom-20 left-1/2 -translate-x-1/2 gap-2 max-w-4xl overflow-x-auto pb-2">
              {filteredGallery.slice(Math.max(0, selectedIndex - 4), Math.min(filteredGallery.length, selectedIndex + 5)).map((item, idx) => {
                const actualIndex = Math.max(0, selectedIndex - 4) + idx;
                return (
                  <button
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIndex(actualIndex);
                      setSelectedImage(item);
                    }}
                    className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden transition-all ${
                      actualIndex === selectedIndex ? 'ring-2 ring-amber-500 opacity-100' : 'opacity-50 hover:opacity-75'
                    }`}
                  >
                    <img src={optimized(item.image, 400)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
